import {AzureLayer, getMinAndMaxZoomOpts} from "@esWebAPI/es/map/layer/azure/AzureLayer";
import { ILayerView } from "@esWebAPI/es/model/Layer";
import { ColorThematic } from "@esWebAPI/es/model/Thematic";
import {layer, LineLayerOptions, Map, source} from "azure-maps-control";
import {DatasourceManager} from "@esWebAPI/es/map/module/DatasourceManager";
import {ColorThematicStyler} from "@esWebAPI/es/map/styler/ColorThematicStyler";
import {DataDrivenPropertyValueSpecification} from "@maplibre/maplibre-gl-style-spec";

export class AzureLineLayer implements AzureLayer {

    private readonly _map: Map;
    private readonly datasourceManager: DatasourceManager
    private readonly colorStyler: ColorThematicStyler;

    private _layerView: ILayerView;

    private datasourceId: string|undefined;
    private _layer: layer.LineLayer|undefined;
    private colorThematic: ColorThematic|undefined;

    constructor(map: Map, layerView: ILayerView, datasourceManager: DatasourceManager, colorStyler: ColorThematicStyler) {
        this._map = map;
        this._layerView = layerView;
        this.datasourceManager = datasourceManager;
        this.colorStyler = colorStyler;
    }

    private async updateThematics(): Promise<void> {
        this.colorThematic = await this.colorStyler.getThematic(this._layerView);
    }

    private getOptions() {

        let defaultStyle = this._layerView.styleSettings.defaultStyle;
        if (defaultStyle.$type === "SymbolAndPolygonStyle") {
            defaultStyle = defaultStyle.polygonStyle;
        }

        const minMaxOpts = getMinAndMaxZoomOpts(this._layerView);

        const strokeColor = this.colorStyler.getStrokeHex(this._layerView, this.colorThematic);
        const strokeOpacity = this.colorStyler.getStrokeOpacity(this._layerView);

        const strokeWidth = defaultStyle.line.width;
        return {
            sourceLayer: this.datasourceId && this.datasourceManager.isPbf(this.datasourceId) ? 'es' : undefined,
            strokeColor,
            strokeWidth,
            strokeOpacity,
            visible: this._layerView.enabled,
            minZoom: minMaxOpts.minZoom,
            maxZoom: minMaxOpts.maxZoom
        } as LineLayerOptions;
    }

    public async create(datasource: source.Source, colorThematic?: ColorThematic): Promise<void> {
        this.datasourceId = datasource.getId();
        this.colorThematic = colorThematic;
        this._layer = new layer.LineLayer(datasource.getId(), this.getLayerId(), this.getOptions());
        this._map.layers.add(this._layer);
    }

    public async recreate(layerView: ILayerView, datasource: source.Source, colorThematic?: ColorThematic) {
        this._layerView = layerView;
        return this.create(datasource, colorThematic);
    }

    async update(layerView: ILayerView): Promise<void> {
        this._layerView = layerView;
        await this.updateThematics();
        let options = this.getOptions();
        this._layer?.setOptions(options);
    }

    public hideFeature(featureId: number): void {
        const strokeOpacity = this.colorStyler.getStrokeOpacity(this._layerView) ?? 1;
        const opacity = ['case', ['==', ['id'], featureId], 0, strokeOpacity] as DataDrivenPropertyValueSpecification<number>;
        this._layer?.setOptions({strokeOpacity: opacity});
    }

    public showFeatures(): void {
        const strokeOpacity = this.colorStyler.getStrokeOpacity(this._layerView);
        this._layer?.setOptions({strokeOpacity: strokeOpacity});
    }

    public remove(): void {
        if (this._layer && this._map.layers.getLayerById(this._layer.getId())) {
            this._map.layers.remove(this._layer.getId());
        }
        this._layer = undefined;
    }

    public hide(): void {
        this._layer?.setOptions({visible: false});
    }
    public show(): void {
        this._layer?.setOptions({visible: true});
    }

    getLayerId(): string {
        return "line_" + this._layerView.id;
    }

}